// Kirish holati: kim kirgan va u platforma adminimi.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '@onepos/client';
import { api } from './api';
import type { PlatformMe } from './types';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out'; message?: string }
  | { status: 'not_admin'; email: string | null }
  | { status: 'ready'; me: PlatformMe };

interface AuthContextValue {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Tokenni yangilab, admin ma'lumotini qayta oladi (yordam rejimi o'zgargach) */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadMe(): Promise<AuthState> {
  if (!api.session) return { status: 'signed_out' };
  try {
    const me = await api.rpc<PlatformMe | null>('platform_me');
    if (!me) return { status: 'not_admin', email: api.session?.user.email ?? null };
    return { status: 'ready', me };
  } catch (e) {
    if (e instanceof ApiError && e.code === 'NETWORK') throw e;
    return { status: 'signed_out', message: e instanceof Error ? e.message : undefined };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    const boot = async () => {
      try {
        const s = await loadMe();
        if (alive) setState(s);
      } catch (e) {
        if (alive) setState({ status: 'signed_out', message: e instanceof Error ? e.message : undefined });
      }
    };
    void boot();
    const off = api.onChange((session) => {
      if (!session) setState((prev) => (prev.status === 'signed_out' ? prev : { status: 'signed_out' }));
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await api.signIn(email, password);
    setState(await loadMe());
  }, []);

  const signOut = useCallback(async () => {
    await api.signOut();
    setState({ status: 'signed_out' });
  }, []);

  const refreshMe = useCallback(async () => {
    await api.refresh();
    setState(await loadMe());
  }, []);

  const value = useMemo(() => ({ state, signIn, signOut, refreshMe }), [state, signIn, signOut, refreshMe]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthProvider yo‘q');
  return ctx;
}

/** Faqat kirgan admin uchun sahifalarda */
export function useMe(): PlatformMe {
  const { state } = useAuth();
  if (state.status !== 'ready') throw new Error('Admin kirmagan');
  return state.me;
}
